import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { commands } from "../../../lib/bindings";
import { } from "@tauri-apps/plugin-dialog";
import { convertFileSrc } from "@tauri-apps/api/core";
import { cn } from "../../../utils/cn";
import { getPositionableClasses } from "../../util/get_positional_classes";
import { getTimestampSourceId } from "../../util/timestamp_utils/get_timestamp_source_id";
import { MediaPlayerProps } from "../../types";

export const NoFileProvided = ({
    componentLabel = "video",
}: {
    componentLabel?: string;
}): ReactNode => {
    return (
        <div className="p-4 border text-red-500 dark:text-red-400">
            {`The ${componentLabel} component requires a 'file' property.`}
        </div>
    );
};

const VideoComponent = ({
    fsPath,
    className,
    id,
    autoPlay,
}: {
    fsPath: string;
    className: string;
    id?: string;
    autoPlay?: boolean;
}): ReactNode => {
    const [data, setData] = useState<null | string>(null);
    useEffect(() => {
        setData(convertFileSrc(fsPath));
    }, [fsPath]);
    if (data === null) {
        return null;
    }
    return (
        <video
            autoPlay={autoPlay}
            id={getTimestampSourceId("video", id)}
            controls
            src={data}
            className={className}
        />
    );
};

export const Video = ({
    file,
    basePath,
    id,
    autoPlay,
    ...props
}: MediaPlayerProps): ReactNode => {
    const [fullPath, setFullPath] = useState<string | null>(null);
    const positionableClasses = useMemo(
        () => getPositionableClasses(props),
        [props]
    );
    const parsePath = async (fp: string, bp: string): Promise<void> => {
        const parsedPath = await commands.normalizePath(fp, bp);
        setFullPath(parsedPath);
    };
    useEffect(() => {
        if (file) {
            parsePath(file, basePath);
        }
    }, [file, basePath]);
    if (!file) {
        return <NoFileProvided />;
    }
    if (!fullPath) {
        return null;
    }
    return (
        <VideoComponent
            id={id}
            className={cn("", positionableClasses)}
            fsPath={fullPath}
            autoPlay={autoPlay}
        />
    );
};

Video.displayName = "Video";
